import React, { useState, useRef, useEffect, ReactNode, MutableRefObject } from "react";
import { createContext, ReactElement, useContext } from "react";
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { Box, Button, Typography, Chip, Alert, Collapse, AlertTitle, IconButton } from '@mui/material';
import { NavBar1 } from './components/navbar/NavBar1';
import Toolbar from '@mui/material/Toolbar';
import { MapBars } from './components/map/MapBars'
//import { Authenticator, useAuthenticator } from "react-oidc-context";
import { CircularProgress } from "@mui/material";
import { getDisclaimerCookie, isErrorStatus, isHttpOrNetworkError, loadImage, setDisclaimerCookie, stringifyOidcError, toStringRepresentation } from './app/utils'

import { AddPinChip } from './components/profile/AddPinChip'
import { showDisclaimer, showProfile, showSurvey, showDeleteAccount, showDataDeletionInstructions, showBanner, setBcanStudy, setResearchConnect } from "./features/dialogSlice";
import { Outlet, useLocation, useNavigate, useMatches } from "react-router-dom";
import { getVersion, useAppSelector, useAuthProfile } from "./app/hooks"
import { useAuth, useAutoSignin } from 'react-oidc-context';
import { CircularProgressCentered } from "./app/components/sharedcomponents";
import { BottomSheet, BottomSheetRef } from 'react-spring-bottom-sheet'
import 'react-spring-bottom-sheet/dist/style.css'
import './App.css'
import { useMediaQuery, useTheme } from "@mui/material";
import { AddBox } from "@mui/icons-material";

import { QueryStatus } from "@reduxjs/toolkit/query";
import { RootState } from './app/store';
import { Condition, coonditionContext } from "./index";

import { LogoutDialog } from "./components/iam/LogoutDialog";

import { useLogToBackEndMutation } from "./features/utilitySlice";
import { AppBody } from "./AppBody";

import { selectAuthMethod } from "./features/userTokenManagementSlice";

export const RightSideUIContext = createContext<any>({});


interface LogOutReason { baseName: string, message?: string, code?: number, type: 'error' | 'user_not_found' | 'authenticator_error', timeStamp: string }

//////////////    App

type RouteFlags = {
  callback?: boolean;
  out?: boolean;
  noBanner?: boolean;
  bcanStudy?: boolean;
  researchConnect?: boolean;
  deleteProfile?: boolean;
  dataDeletionInstructions?: boolean;
  messages?: boolean
  showProfileFromUrl?: boolean
  scrollable?: boolean
};
interface AppProps { children?: React.ReactNode, deleteProfile?: boolean, showProfileFromUrl?: boolean, dataDeletionInstructions?: boolean, messages?: boolean, callback?: boolean, out?: boolean, noBanner?: boolean, noAuth?: boolean }
export const AppLayout: React.FC<AppProps> = ({ children }) => {
  const matches = useMatches();
  const routeFlags = React.useMemo(() => {
    return matches.reduce((acc, m) => {
      if (m.handle) Object.assign(acc, m.handle);
      return acc;
    }, {} as RouteFlags);
  }, [matches]);
  const { callback = false, out = false, noBanner = false, bcanStudy = false, researchConnect = false, deleteProfile = false, messages = false, showProfileFromUrl = false, dataDeletionInstructions = false, scrollable = false} = routeFlags;

  const { authStatus, authUser, profile, isLoading, isSuccess, isError, error,     authUser: user,// isAuthenticated, errorAuthenticator, 
    isLoading:isLoadingAuth,// eventsAuthenticator, //isSignOutErrorButLoggedIn,
        logoutFromAnyAuthMethod
  } = useAuthProfile()

  const location = useLocation();
  const dispatch = useDispatch()
  const { condition, baseName } = React.useContext<Condition>(coonditionContext);
  const [patientPinPNG, setPatientPinPNG] = useState<string | null>(null);
  //const redirected = useRef<boolean | undefined>(false); // 🔐 flag to prevent looping
  const navigate = useNavigate();

  const auth = useAuth();
  const [logoutMessage, setLogoutMessage] = React.useState<{ message: string, hasBeenLoggedOut: boolean } | null>(null);
  const [logToBackEnd, { }] = useLogToBackEndMutation();
  // Route-flag driven — re-runs whenever the active route's handle flags change, NOT just on mount. This matters
  // because AppLayout does NOT remount across sibling routes: after a federated-login redirect we land on /callback
  // (noBanner/bcanStudy false), then client-side navigate INTO /bcanstudy — a mount-only effect would miss that
  // second route and leave the banner visible / the study flag unset. noBanner → hide banner; bcanStudy → remember
  // the study entry. Neither is turned back off (a later non-study route shouldn't clear a study entry mid-session).
  React.useEffect(() => {
    if (noBanner) {
      dispatch(showBanner(false));
    }
    if (bcanStudy) {
      dispatch(setBcanStudy(true));
    }
    if (researchConnect) {
      dispatch(setResearchConnect(true));
    }
  }, [noBanner, bcanStudy, researchConnect]);

  // logoutReason is a genuine once-on-mount read.
  React.useEffect(() => {
    try {
      const reasonJson = sessionStorage.getItem('logoutReason');
      if (reasonJson) {
        const logOutReason: LogOutReason = JSON.parse(reasonJson ?? '');
        if (logOutReason?.type !== 'user_not_found') {
          setLogoutMessage({ message: logOutReason?.message ?? '', hasBeenLoggedOut: true }); // Update default message
        }
      }
    } catch (e) {
      console.error("Failed to read or parse logout reason from sessionStorage:", e);
      sessionStorage.removeItem('logoutReason'); // Clear potentially corrupted item
    }
  }, []); // Empty dependency array ensures this runs only once on mount

  React.useEffect(() => {
    const fetchImage = async () => {
      if (!patientPinPNG) {
        let path = await loadImage(`patienticon${baseName}.png`, `patienticon.png`);
        //path = await loadImage(`patienticon.png`)                
        setPatientPinPNG(path ?? await loadImage(`patienticon.png`))
      }
    };
    fetchImage();
  }, [baseName]); // Re-run effect if baseName changes


  React.useEffect(() => {
    // Select the first meta tag with a name attribute
    const metaTag = document.querySelector('meta[name]');
    if (metaTag) {
      metaTag.setAttribute("name", `${condition} Patient Connect`);
      metaTag.setAttribute("content", `${condition} Patient Connect`);
    }
    document.title = `${condition} Patient Connect`;
    const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
    if (link && patientPinPNG) {
      link.href = patientPinPNG as string; // Set the imported icon dynamically
    }
  }, [patientPinPNG]);

  React.useEffect(() => {
    if (showProfileFromUrl)
      dispatch(showProfile({ isForced: true }))
  }, [showProfileFromUrl])

  React.useEffect(() => {
    if (deleteProfile)
      dispatch(showDeleteAccount(true))
  }, [deleteProfile])

  React.useEffect(() => {
    if (dataDeletionInstructions) {
      dispatch(showDataDeletionInstructions(true))
    }
  }, [dataDeletionInstructions])

  // The account-delete modal now mounts in RootLayout (above MagicLinkGuard) so it renders on every route —
  // map AND HealthWallet — instead of only under AppLayout. Nothing to short-circuit here anymore.

  // not needed? TODO  figure out why the left drawer and the map are rendering spinner while auth in progress
  if (authStatus === 'configuring' || isLoading){
    //console.info( "AppLayout: authStatus === 'configuring' || isLoading){")
    return (<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', width: '100vw', flexDirection: 'row' }}>
      <CircularProgress />
    </div>)
  }

  const shouldActivateAutoSignin = !out && user && !isLoadingAuth;
  return (
    <Box id='App-Container'
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: scrollable ? 'auto' : '100svh',
        minHeight: '100svh',
        width: '100vw',
        //minHeight: '300vh',
        //backgroundColor: 'blue'
      }}>
      <RightSideUIContext.Provider value={{ linkData: location.state, deleteProfile, messages, callback }}>
        <NavBar1 />
        <Toolbar />
        {logoutMessage && <LogoutDialog
          message={logoutMessage?.message}
          hasBeenLoggedOut={logoutMessage?.hasBeenLoggedOut ?? false}
          onClose={() => setLogoutMessage(null)}
        />}
        <Box id='Below-Navbar-flexgrow' sx={{
          display: 'flex',
          width: '100%',
          height: '100%',
          background: 'linear-gradient(160deg, #EDE8FF 0%, #E2DAFF 40%, #D6CCF5 100%)',
          flexGrow: 1,
          minHeight: 0,
          flexShrink: scrollable ? 0 : 1,
          overflow: scrollable ? 'visible' : 'hidden',
        }}>
          {
            < >
              {/* {childComponent ? <>{childComponent}</> : <AppBody />} */}
              {children ?? <Outlet />}
              {/* <Outlet />  */}
            </>
          }
        </Box>
      </RightSideUIContext.Provider>
    </Box>
  );
}
interface AuthLayoutProps {
  children?: ReactNode;
}

export const AuthLayout: React.FC<AuthLayoutProps> = ({ children }) => {
  return (
    <Box
      id="App-Container"
      sx={{
        position: 'relative',
        inset: 0,
        width: '100vw',
        height: '100dvh',       // 👈 keyboard-aware
        overflowY: 'auto',      // 👈 ONLY SCROLLER
        display: 'flex',
        justifyContent: 'center',
        WebkitOverflowScrolling: 'touch',
        background: 'linear-gradient(135deg, #e8e0f8 0%, #d4c8f0 25%, #c3b5e8 50%, #b8a9e3 75%, #e0d9f5 100%)',
      }}
    >
      {/* Auth-specific UI, could be simpler */}
      {/* <main> */}
        {children ?? <Outlet />}
      {/* </main> */}
    </Box>
  );
};

